import { ComboboxEmpty } from "./ComboboxEmpty";
import {
  ComboboxMultipleProps,
  ComboboxProps,
  ComboboxSingleProps,
} from "./ComboboxTypes";
import { ComboboxSelect } from "./ComboboxSelect";
import { ComboboxContent } from "./ComboboxContent";
import { ComboboxSearchField, ComboboxSelectTrigger } from "./ComboboxTrigger";
import { ComboboxItem, ComboboxItemAddNew } from "./ComboboxItem";
import { ComboboxList } from "./ComboboxList";
import {
  ADD_NEW,
  getScrollParent,
  isMultiple,
  SELECT_ALL,
} from "./internal/ComboboxUtils";
import {
  useDownshiftEnvironment,
  useLayoutPropsUtil,
} from "../../internal/hooks";
import cx from "classnames";
import styles from "./Combobox.module.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCombobox as useDownshiftCombobox,
  useMultipleSelection as useDownshiftMultipleSelection,
  UseComboboxReturnValue as UseDownshiftComboboxReturnValue,
  UseComboboxStateChangeTypes,
} from "downshift";
import {
  defaultItemToString,
  defaultItemToKey,
} from "./internal/ComboboxUtils";
import { useGroups } from "./internal/useGroups";
import { matchSorter } from "match-sorter";
import { ComboboxContext } from "./internal/ComboboxContext";
import Popover from "../Popover";

const DEBUG_STATE_REDUCERS = false;

const ComboboxSingle = function <Item>(props: ComboboxSingleProps<Item>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    disabled,
    readOnly,
    items: itemsProp,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    filterOptions,
    disableFilter = false,
    loading = false,
    minPopoverHeight,
    root,
    onInputValueChange,
    environment: environmentProp,
    groupBy,
    groupToString,
    groupSorter,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [items, setItems] = useState(itemsProp);
  const [hasAddNew, setHasAddNew] = useState(false);
  const [onSelectItemAddNew, setOnSelectItemAddNew] = useState<
    | ((
        inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
      ) => void)
    | undefined
  >(undefined);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [, setForceClearInputValueOnSelectItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);

  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const { orderedItems: allItemsOrderedByGroup } = useGroups({
    items: itemsProp,
    groupBy,
    groupToString,
    groupSorter,
  });

  const { groups: searchedGroups, orderedItems: searchedItemsOrderedByGroup } =
    useGroups({
      items: items,
      groupBy,
      groupToString,
      groupSorter,
    });

  const showAddNew = hasAddNew && !hasExactMatch;
  const combinedItems = useMemo(
    () =>
      showAddNew
        ? [...searchedItemsOrderedByGroup, ADD_NEW as Item]
        : searchedItemsOrderedByGroup,
    [searchedItemsOrderedByGroup, showAddNew],
  );

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    setInputValue,
    inputValue,
    closeMenu,
  } = useDownshiftCombobox<Item>({
    ...rest,
    items: combinedItems,
    itemToString,
    itemToKey,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // keep Combobox open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftCombobox.stateChangeTypes.ItemClick:
          if (changes.selectedItem === ADD_NEW) {
            // if we're adding a new item, don't change the selectedItem
            // and just call the event handler
            // TODO: Figure out a way to do this without having side-effects in
            //       the stateReducer
            const stateInputValue = state.inputValue;
            setTimeout(() => {
              onSelectItemAddNew?.(stateInputValue);
            }, 250); // setTimeout so we don't setState during render

            return {
              ...changes,
              selectedItem: state.selectedItem,
              inputValue: "",
            };
          } else {
            const forceClose = forceCloseOnSelectItems.includes(
              changes.selectedItem as Item,
            );

            return {
              ...changes,
              isOpen:
                open != null
                  ? open
                  : forceClose
                    ? false
                    : disableCloseOnSelectItem
                      ? true
                      : changes.isOpen,
              highlightedIndex: disableCloseOnSelectItem
                ? itemToString(state.selectedItem) === state.inputValue ||
                  !state.inputValue
                  ? state.highlightedIndex
                  : 0
                : changes.highlightedIndex,
            };
          }

        // prevent this from closing the popover by default
        case useDownshiftCombobox.stateChangeTypes.InputClick:
          return { ...changes, isOpen: open != null ? open : true };

        default:
          return changes;
      }
    },
    onInputValueChange:
      onInputValueChange ??
      (({ inputValue, type, selectedItem }) => {
        const inputValueMatchesSelectedItem =
          selectedItem != null && inputValue === itemToString(selectedItem);
        const inputValueIsEmpty = inputValue == null || inputValue === "";

        if (
          type === useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter ||
          type === useDownshiftCombobox.stateChangeTypes.ItemClick
        ) {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        } else if (
          !inputValueMatchesSelectedItem &&
          !inputValueIsEmpty &&
          !disableFilter
        ) {
          const filteredItems = matchSorter(
            allItemsOrderedByGroup,
            inputValue,
            filterOptions,
          );
          setItems(filteredItems);
          setHasExactMatch(
            matchSorter(filteredItems, inputValue, {
              ...filterOptions,
              threshold: matchSorter.rankings.EQUAL,
            }).length > 0,
          );
        } else {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        }
      }),
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;

      if (onChange != null)
        onChange(selectedItem != null ? selectedItem : null);
    },
    onIsOpenChange({ isOpen, inputValue, selectedItem }) {
      if (isOpen === false) {
        // reset the filtering whenever we close the dropdown
        // Delaying input reset timing to be after Popover close to avoid
        // list showing full version for a split second before closing
        setTimeout(() => {
          setItems(allItemsOrderedByGroup);

          if (inputValue === "") {
            // special case: if the search field is empty, remove the selected item
            selectItem(null);
          } else {
            // otherwise, force the text to match the selected item

            setInputValue(
              selectedItem != null ? itemToString(selectedItem) : "",
            );
          }
        }, 200);
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  // prevent prop sync useEffects from running on intiial render
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  // synchronize filtering state when "items" prop changes
  useEffect(() => {
    if (firstRenderRef.current) return;

    const inputValueMatchesSelectedItem =
      selectedItem != null && inputValue === itemToString(selectedItem);
    const inputValueIsEmpty = inputValue == null || inputValue === "";

    // When dropdown is closed, sync inputValue with controlled selectedItem
    if (!isOpen && selectedItem != null && !inputValueMatchesSelectedItem) {
      setInputValue(itemToString(selectedItem));
      setItems(allItemsOrderedByGroup);
      setHasExactMatch(true);
      return;
    }

    if (!isOpen && selectedItem == null && !inputValueIsEmpty) {
      setInputValue("");
      setItems(allItemsOrderedByGroup);
      setHasExactMatch(true);
      return;
    }

    // Handle filtering when user is typing
    if (
      !inputValueMatchesSelectedItem &&
      !inputValueIsEmpty &&
      !disableFilter
    ) {
      const filteredItems = matchSorter(
        allItemsOrderedByGroup,
        inputValue,
        filterOptions,
      );
      setItems(filteredItems);
      setHasExactMatch(
        matchSorter(filteredItems, inputValue, {
          ...filterOptions,
          threshold: matchSorter.rankings.EQUAL,
        }).length > 0,
      );
    } else {
      setItems(allItemsOrderedByGroup);
      setHasExactMatch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItemsOrderedByGroup, selectedItem, inputValue, isOpen]);

  const contextValue = useMemo(
    () => ({
      multiple: false,
      select: false,
      items: searchedItemsOrderedByGroup,
      itemToString,
      itemToKey,
      isOpen,
      getLabelProps,
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      selectedItem,
      selectedItems: [],
      disabledItems,
      setDisabledItems,
      setForceCloseOnSelectItems,
      setForceClearInputValueOnSelectItems,
      getSelectedItemProps: undefined,
      getDropdownProps: undefined,
      selectItem,
      inputValue,
      isDisabled,
      isReadOnly,
      setIsReadOnly,
      setIsDisabled,
      hasDisabledPopover,
      setHasDisabledPopover,
      hasEmpty,
      setHasEmpty,
      hasAddNew,
      setHasAddNew,
      onSelectItemAddNew,
      setOnSelectItemAddNew,
      hasExactMatch,
      setHasExactMatch,
      loading,
      referenceRef,
      contentRef,
      listRef,
      closeMenu,
      groupBy,
      groupToString,
      groups: searchedGroups,
    }),
    [
      searchedItemsOrderedByGroup,
      itemToString,
      itemToKey,
      isOpen,
      getLabelProps,
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      selectedItem,
      disabledItems,
      setDisabledItems,
      setForceCloseOnSelectItems,
      setForceClearInputValueOnSelectItems,
      selectItem,
      inputValue,
      isDisabled,
      isReadOnly,
      setIsReadOnly,
      setIsDisabled,
      hasDisabledPopover,
      setHasDisabledPopover,
      hasEmpty,
      setHasEmpty,
      hasAddNew,
      setHasAddNew,
      onSelectItemAddNew,
      setOnSelectItemAddNew,
      hasExactMatch,
      setHasExactMatch,
      loading,
      referenceRef,
      contentRef,
      listRef,
      closeMenu,
      groupBy,
      groupToString,
      searchedGroups,
    ],
  );

  return (
    <ComboboxContext.Provider value={contextValue}>
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onClickOutside={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

const ComboboxMultiple = function <Item>(props: ComboboxMultipleProps<Item>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    items: itemsProp,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    onSelectedItemsChange,
    onStateChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    disableClearInputValueOnSelectItem = false,
    filterOptions,
    disableFilter = false,
    loading = false,
    minPopoverHeight,
    root,
    disabled,
    readOnly,
    stateReducer: _,
    onInputValueChange,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp,
    selectAll,
    groupBy,
    groupToString,
    groupSorter,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [items, setItems] = useState(itemsProp);
  const [hasAddNew, setHasAddNew] = useState(false);
  const [onSelectItemAddNew, setOnSelectItemAddNew] = useState<
    | ((
        inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
      ) => void)
    | undefined
  >(undefined);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [
    forceClearInputValueOnSelectItems,
    setForceClearInputValueOnSelectItems,
  ] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  // Downshift's useCombobox and useMultipleSelection have a kinda sorta
  // circular dependency, so storing setInputValue here to allow setting the
  // inputValue in the useMultipleSelection change handlers
  let storedSetInputValue: ((inputValue: string) => void) | undefined =
    undefined;

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);
  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const { orderedItems: allItemsOrderedByGroup } = useGroups({
    items: itemsProp,
    groupBy,
    groupToString,
    groupSorter,
  });

  const { groups: searchedGroups, orderedItems: searchedItemsOrderedByGroup } =
    useGroups({
      items: items,
      groupBy,
      groupToString,
      groupSorter,
    });

  const showAddNew = hasAddNew && !hasExactMatch;
  const combinedItems = useMemo(() => {
    let items = searchedItemsOrderedByGroup;
    if (showAddNew) {
      items = [...items, ADD_NEW as Item];
    }
    if (selectAll) {
      items = [SELECT_ALL as Item, ...items];
    }
    return items;
  }, [searchedItemsOrderedByGroup, showAddNew, selectAll]);

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    getSelectedItemProps,
    getDropdownProps,
    reset,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useDownshiftMultipleSelection<Item>({
    itemToKey,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp ?? environment,
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // the Chip itself will handle "Backspace" and "Delete"
        // just return previous state
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownBackspace:
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownDelete:
          return state;

        case useDownshiftMultipleSelection.stateChangeTypes
          .FunctionRemoveSelectedItem:
          return { ...changes, activeIndex: -1 };

        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          return { ...changes, selectedItems: [] };

        default:
          return changes;
      }
    },
    onStateChange(changes) {
      const { type } = changes;

      switch (type) {
        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          storedSetInputValue?.("");
          break;
      }

      onStateChange?.(changes);
    },
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange != null) onChange(selectedItems);
      if (onSelectedItemsChange != null) onSelectedItemsChange(changes);
    },
  });

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    setInputValue,
    inputValue,
    closeMenu,
  } = useDownshiftCombobox<Item>({
    ...rest,
    items: combinedItems,
    itemToString,
    itemToKey,
    selectedItem: null,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // keep Combobox open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: state.inputValue,
            selectedItem: null,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftCombobox.stateChangeTypes.ItemClick:
          if (changes.selectedItem === ADD_NEW) {
            // if we're adding a new item, don't change the selectedItem
            // and just call the event handler
            // TODO: Figure out a way to do this without having side-effects in
            //       the stateReducer
            const stateInputValue = state.inputValue;
            setTimeout(() => {
              onSelectItemAddNew?.(stateInputValue);
            }, 250); // setTimeout so we don't setState during render

            return {
              ...changes,
              selectedItem: state.selectedItem,
              inputValue: "",
            };
          } else if (changes.selectedItem === SELECT_ALL) {
            selectAll?.onSelection();
            return {
              ...changes,
              selectedItem: null,
            };
          } else {
            const forceClose = forceCloseOnSelectItems.includes(
              changes.selectedItem as Item,
            );
            const forceClearInputValue =
              forceClearInputValueOnSelectItems.includes(
                changes.selectedItem as Item,
              );

            return {
              ...changes,
              isOpen:
                open != null
                  ? open
                  : forceClose
                    ? false
                    : disableCloseOnSelectItem
                      ? true
                      : changes.isOpen,
              highlightedIndex: forceClearInputValue
                ? 0
                : disableCloseOnSelectItem
                  ? disableClearInputValueOnSelectItem || !state.inputValue
                    ? state.highlightedIndex
                    : 0
                  : changes.highlightedIndex,
              inputValue: forceClearInputValue
                ? ""
                : disableClearInputValueOnSelectItem
                  ? state.inputValue
                  : "",
            };
          }

        // prevent this from closing the popover by default
        case useDownshiftCombobox.stateChangeTypes.InputClick:
          return { ...changes, isOpen: open != null ? open : true };

        // prevent this from clearing the input value by default
        case useDownshiftCombobox.stateChangeTypes
          .ControlledPropUpdatedSelectedItem:
          return { ...changes, inputValue: state.inputValue };

        default:
          return changes;
      }
    },
    onStateChange(changes) {
      const { type, selectedItem: newSelectedItem } = changes;

      switch (type) {
        case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftCombobox.stateChangeTypes.ItemClick:
        case useDownshiftCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem != null) {
            const itemAlreadySelected = selectedItems.some(
              (item) => itemToKey(item) === itemToKey(newSelectedItem),
            );

            if (itemAlreadySelected) {
              removeSelectedItem(newSelectedItem);
            } else {
              addSelectedItem(newSelectedItem);
            }
          }
          break;

        default:
          break;
      }

      onStateChange?.(changes);
    },
    onInputValueChange:
      onInputValueChange ??
      (({ inputValue, ...changes }) => {
        const { type, selectedItem } = changes;

        const inputValueMatchesSelectedItem =
          selectedItem != null && inputValue === itemToString(selectedItem);
        const inputValueIsEmpty = inputValue == null || inputValue === "";

        if (
          type === useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter ||
          type === useDownshiftCombobox.stateChangeTypes.ItemClick
        ) {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        } else if (
          !inputValueMatchesSelectedItem &&
          !inputValueIsEmpty &&
          !disableFilter
        ) {
          const filteredItems = matchSorter(
            allItemsOrderedByGroup,
            inputValue,
            filterOptions,
          );
          setItems(filteredItems);
          setHasExactMatch(
            matchSorter(filteredItems, inputValue, {
              ...filterOptions,
              threshold: matchSorter.rankings.EQUAL,
            }).length > 0,
          );
        } else {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        }
      }),
    onIsOpenChange({ isOpen }) {
      if (isOpen === false) {
        // Delaying input reset timing to be after Popover close to avoid
        // list showing full version for a split second before closing
        setTimeout(() => {
          setInputValue("");

          setItems(allItemsOrderedByGroup);
        }, 200);
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
    onHighlightedIndexChange(changes) {
      if (
        selectAll &&
        changes.highlightedIndex === 0 &&
        (
          [
            useDownshiftCombobox.stateChangeTypes.InputKeyDownHome,
            useDownshiftCombobox.stateChangeTypes.InputKeyDownEnd,
            useDownshiftCombobox.stateChangeTypes.InputKeyDownArrowDown,
            useDownshiftCombobox.stateChangeTypes.InputKeyDownArrowUp,
            useDownshiftCombobox.stateChangeTypes.InputKeyDownPageDown,
            useDownshiftCombobox.stateChangeTypes.InputKeyDownPageUp,
          ] as UseComboboxStateChangeTypes[]
        ).includes(changes.type) &&
        listRef.current
      ) {
        // Scroll to the top of the list when select all is highlighted
        const scrollParent = getScrollParent(listRef.current);
        if (scrollParent) {
          scrollParent.scrollTop = 0;
        }
      }
    },
  });

  // Now that useDownshiftCombobox has returned the setInputValue function,
  // we can store it here to call later inside of useDownshiftMultipleSelection
  storedSetInputValue = setInputValue;

  // prevent prop sync useEffects from running on intiial render
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  // synchronize filtering state when "items" prop changes
  useEffect(() => {
    if (firstRenderRef.current) return;

    setTimeout(() => {
      const inputValueIsEmpty = inputValue == null || inputValue === "";

      if (!inputValueIsEmpty && !disableFilter) {
        const filteredItems = matchSorter(itemsProp, inputValue, filterOptions);
        setItems(filteredItems);
        setHasExactMatch(
          matchSorter(filteredItems, inputValue, {
            ...filterOptions,
            threshold: matchSorter.rankings.EQUAL,
          }).length > 0,
        );
      } else {
        setItems(itemsProp);
        setHasExactMatch(true);
      }
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsProp]);

  const contextValue = useMemo(
    () => ({
      multiple: true,
      select: false,
      items: combinedItems,
      itemToString,
      itemToKey,
      isOpen,
      getLabelProps,
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      selectedItem,
      selectedItems,
      disabledItems,
      setDisabledItems,
      setForceCloseOnSelectItems,
      setForceClearInputValueOnSelectItems,
      getSelectedItemProps,
      getDropdownProps,
      reset,
      addSelectedItem,
      removeSelectedItem,
      setSelectedItems,
      selectItem,
      inputValue,
      isDisabled,
      isReadOnly,
      setIsReadOnly,
      setIsDisabled,
      hasDisabledPopover,
      setHasDisabledPopover,
      hasEmpty,
      setHasEmpty,
      hasAddNew,
      setHasAddNew,
      onSelectItemAddNew,
      setOnSelectItemAddNew,
      hasExactMatch,
      setHasExactMatch,
      loading,
      referenceRef,
      contentRef,
      listRef,
      closeMenu,
      groupBy,
      groupToString,
      groups: searchedGroups,
      selectAll,
    }),
    [
      combinedItems,
      itemToString,
      itemToKey,
      isOpen,
      getLabelProps,
      getInputProps,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      selectedItem,
      selectedItems,
      disabledItems,
      setDisabledItems,
      setForceCloseOnSelectItems,
      setForceClearInputValueOnSelectItems,
      getSelectedItemProps,
      getDropdownProps,
      reset,
      addSelectedItem,
      removeSelectedItem,
      setSelectedItems,
      selectItem,
      inputValue,
      isDisabled,
      isReadOnly,
      setIsReadOnly,
      setIsDisabled,
      hasDisabledPopover,
      setHasDisabledPopover,
      hasEmpty,
      setHasEmpty,
      hasAddNew,
      setHasAddNew,
      onSelectItemAddNew,
      setOnSelectItemAddNew,
      hasExactMatch,
      setHasExactMatch,
      loading,
      referenceRef,
      contentRef,
      listRef,
      closeMenu,
      groupBy,
      groupToString,
      searchedGroups,
      selectAll,
    ],
  );

  return (
    <ComboboxContext.Provider value={contextValue}>
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={!isDisabled && isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onClickOutside={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

// multi-select and single-select need to be separate components because
// we can't call hooks conditionally
/**
 * Combobox component for selecting items with search and filtering capabilities.
 *
 * Features:
 * - Single and multiple item selection
 * - Search and filtering with configurable options
 * - Optional "Add New" item functionality
 * - Grouping support with custom group labels and sorting
 * - Loading states with spinner indicators
 * - Disabled and read-only states
 * - Customizable popover behavior and positioning
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Combobox
 *   items={items}
 *   itemToString={(item) => item.name}
 *   onChange={setSelectedItem}
 *   filterOptions={{ keys: ["name"] }}
 * >
 *   <Combobox.SearchField label="Select an item" />
 *   <Combobox.Content>
 *     {({ items }) => (
 *       <Combobox.List>
 *         {items.map((item, i) => (
 *           <Combobox.Item key={item.id} item={item} index={i}>
 *             {item.name}
 *           </Combobox.Item>
 *         ))}
 *       </Combobox.List>
 *     )}
 *   </Combobox.Content>
 * </Combobox>
 */
export const Combobox = Object.assign(
  function ComboboxInner<Item>(props: ComboboxProps<Item>) {
    if (isMultiple(props)) {
      return <ComboboxMultiple<Item> {...props} />;
    } else {
      return <ComboboxSingle<Item> {...props} />;
    }
  },
  {
    /**
     * ComboboxSelect component for selecting items from a predefined list.
     *
     * Features:
     * - Single and multiple item selection from predefined options
     * - Dropdown-style selection interface
     * - Grouping support with custom group labels and sorting
     * - Loading states with spinner indicators
     * - Disabled and read-only states
     * - Customizable popover behavior and positioning
     * - Full accessibility support with ARIA attributes
     * - Keyboard navigation support
     * - Layout utility props for positioning and spacing
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.Select
     *   items={items}
     *   itemToString={(item) => item.name}
     *   onChange={setSelectedItem}
     * >
     *   <Combobox.SelectTrigger label="Select an item" />
     *   <Combobox.Content>
     *     {({ items }) => (
     *       <Combobox.List>
     *         {items.map((item, i) => (
     *           <Combobox.Item key={item.id} item={item} index={i}>
     *             {item.name}
     *           </Combobox.Item>
     *         ))}
     *       </Combobox.List>
     *     )}
     *   </Combobox.Content>
     * </Combobox.Select>
     */
    Select: ComboboxSelect,
    /**
     * ComboboxSearchField component for the search input field.
     *
     * Features:
     * - Search input with clear button functionality
     * - Supports multiple selection with chip display
     * - Handles keyboard navigation and accessibility
     * - Integrates with combobox state management
     * - Supports custom styling and layout
     * - Manages focus and blur events
     * - Enhanced prefix support for icons and avatars
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.SearchField
     *   label="Search users"
     *   placeholder="Type to search..."
     *   prefix={{ icon: { svg: SearchIcon } }}
     * />
     */
    SearchField: ComboboxSearchField,
    /**
     * ComboboxSelectTrigger component for the select trigger field.
     *
     * Features:
     * - Select input with dropdown trigger
     * - Supports single selection display
     * - Handles keyboard navigation and accessibility
     * - Integrates with combobox state management
     * - Supports custom styling and layout
     * - Manages focus and blur events
     * - Enhanced prefix support for icons and avatars
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.SelectTrigger
     *   label="Select user"
     *   placeholder="Choose a user..."
     *   prefix={{ avatar: { name: "User" } }}
     * />
     */
    SelectTrigger: ComboboxSelectTrigger,
    /**
     * ComboboxContent component for rendering the content area of a combobox.
     *
     * Features:
     * - Supports both popover and inline rendering modes
     * - Provides render props pattern for flexible content rendering
     * - Handles grouped items with proper ARIA labels
     * - Integrates with combobox state management
     * - Supports custom styling and layout
     * - Manages focus and blur events for popover behavior
     *
     * @example
     * <Combobox.Content>
     *   {({ items }) => (
     *     <Combobox.List>
     *       {items.map((item, index) => (
     *         <Combobox.Item key={item.id} item={item} index={index}>
     *           {item.name}
     *         </Combobox.Item>
     *       ))}
     *     </Combobox.List>
     *   )}
     * </Combobox.Content>
     */
    Content: ComboboxContent,
    /**
     * ComboboxList component for rendering the list of items.
     *
     * Features:
     * - Renders list of items with proper accessibility
     * - Supports both single and multiple selection modes
     * - Loading state with spinner
     * - Empty state handling
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.List>
     *   {items.map((item, i) => (
     *     <Combobox.Item key={item.id} item={item} index={i}>
     *       {item.name}
     *     </Combobox.Item>
     *   ))}
     * </Combobox.List>
     */
    List: ComboboxList,
    /**
     * ComboboxItem component for rendering individual items in the list.
     *
     * Features:
     * - Individual item rendering with proper accessibility
     * - Support for disabled state
     * - Custom content rendering
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.Item item={item} index={index}>
     *   <Flex direction="column">
     *     <Text>{item.name}</Text>
     *     <Text size="small" subdued>{item.description}</Text>
     *   </Flex>
     * </Combobox.Item>
     */
    Item: ComboboxItem,
    /**
     * ComboboxItemAddNew component for rendering an "Add New" option.
     *
     * Features:
     * - "Add New" option that appears when no exact match is found
     * - Customizable content rendering
     * - Callback when selected
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.ItemAddNew onSelection={(inputValue) => addNewItem(inputValue)}>
     *   Add "{inputValue}" as new item
     * </Combobox.ItemAddNew>
     */
    ItemAddNew: ComboboxItemAddNew,
    /**
     * ComboboxEmpty component for rendering content when no items are available.
     *
     * Features:
     * - Renders content when list is empty
     * - Customizable empty state message
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Combobox.Empty>
     *   <Text>No items found. Try adjusting your search.</Text>
     * </Combobox.Empty>
     */
    Empty: ComboboxEmpty,
  },
);
export const ComboboxElement = Combobox;
